import type { FontService, FontSubsetRequest } from '@/framework/services/font';
import { normalizeChars } from '@/framework/services/font/subset';
import { MaterialIdsType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { LIST_MARKER_DICT } from '@/version-types/constants';

/**
 * 根据图层 runtime 字体映射解析实际渲染字体子集。
 *
 * @param layer - ILayerProps，当前文本图层。
 * @param fontFamily - string，图层数据中记录的原始字体族。
 * @returns string，已解析的 runtime 字体子集；没有映射时返回原始字体族。
 */
export const resolveFontFamily = (layer: ILayerProps, fontFamily: string): string => {
    const fontFamilyMap = layer.__runtime?.fontFamilyMap || {};
    return fontFamilyMap[fontFamily] || fontFamily;
};

/**
 * 解析图层主文本使用的 runtime 字体子集。
 *
 * @param layer - ILayerProps，当前文本图层。
 * @returns string，优先返回 runtime 字体子集，没有映射时返回图层原始字体族。
 */
export const resolveMainFontFamily = (layer: ILayerProps): string => {
    const data = layer.data as ITextLayerProps;
    const fontFamily = data.fontFamily;

    return resolveFontFamily(layer, fontFamily);
};

export type CollectFontCharsOptions = {
    /**
     * 是否将 list marker 字符集纳入收集，默认 false
     */
    includeListMarker?: boolean;
    /**
     * 额外拼接的预热字符（比如进入编辑前预加载一些常见字符，避免首次输入缺字进行子集加载），默认空字符串
     */
    prewarmChars?: string;
};

/**
 * 收集文本图层各字体族所需的字符集(基础版)。
 *
 * 是"按字体累积字符"的唯一遍历来源,`collectFontSubsetRequests` 与编辑器预热均基于它。
 *
 * @param layer - 图层
 * @param options - 收集选项:是否包含列表 marker、是否附带预热字符
 * @returns 字体到字符集的映射
 */
export const collectFontChars = (
    layer: ILayerProps,
    { includeListMarker = false, prewarmChars = '' }: CollectFontCharsOptions = {},
): Map<string, string> => {
    const data = layer.data as ITextLayerProps;

    const map = new Map<string, string>();

    const append = (fontFamily: string, text: string) => {
        map.set(fontFamily, `${map.get(fontFamily) || ''}${text}`);
    };

    if (data.textContents && data.textContents.length > 0) {
        data.textContents.forEach((item) => {
            if (!item.text) return;
            append(item.fontFamily || data.fontFamily, item.text);
        });
    } else if (data.text) {
        append(data.fontFamily, data.text);
    }

    if (includeListMarker) {
        const { listStyleMaterial, listStyle } = data;
        if (listStyle && listStyle !== 'custom') {
            append(data.fontFamily, LIST_MARKER_DICT[listStyle]);
        } else if (
            listStyle === 'custom' &&
            listStyleMaterial &&
            listStyleMaterial.autoIncrement !== 'none' &&
            layer.__listMarkerFontFamily
        ) {
            append(layer.__listMarkerFontFamily, LIST_MARKER_DICT[listStyleMaterial.autoIncrement]);
        }
    }

    for (const [fontFamily, chars] of map) {
        map.set(fontFamily, normalizeChars(`${chars}${prewarmChars}`));
    }

    return map;
};

/**
 * 收集文本图层所用的字体子集请求(含列表 marker)。
 * @param layer - 图层
 * @returns
 */
export const collectFontSubsetRequests = (layer: ILayerProps): FontSubsetRequest[] =>
    [...collectFontChars(layer, { includeListMarker: true })].map(([fontFamily, chars]) => ({ fontFamily, chars }));

/**
 * 收集文本图层所用的字体集合
 * @param layer - 图层数据
 * @returns
 */
export const collectFontFamilies = (layer: ILayerProps): Set<string> => {
    const { textContents = [], fontFamily } = layer.data as ITextLayerProps;
    const families = new Set<string>([fontFamily]);

    if (textContents.length > 0) {
        textContents.forEach((item) => {
            if (item.fontFamily) families.add(item.fontFamily);
        });
    }

    if (layer.__listMarkerFontFamily) families.add(layer.__listMarkerFontFamily);

    return families;
};

/**
 * 刷新图层 runtime 字体族映射
 * @param layer - 图层
 * @returns
 */
export const refreshRuntimeFontFamilies = (layer: ILayerProps): ILayerProps => {
    const fontFamilyMap = layer.__runtime?.fontFamilyMap;
    if (!fontFamilyMap) return layer;

    const fontFamilies = collectFontFamilies(layer);
    const nextFontFamilyMap = Object.fromEntries(
        Object.entries(fontFamilyMap).filter(([fontFamily]) => fontFamilies.has(fontFamily)),
    );

    return {
        ...layer,
        __runtime: {
            ...layer.__runtime,
            fontFamilyMap: nextFontFamilyMap,
        },
    };
};

/**
 * 获取图层字体 id 列表
 * @param framework - 框架实例
 * @param layer - 图层
 * @returns
 */
export const collectFontMaterialIDs = (fontService: FontService, layer: ILayerProps) => {
    const fontFamilies = collectFontFamilies(layer);

    return Array.from(fontFamilies)
        .map((item) => {
            const font = fontService.getByFamily(item);
            return font ? font.id : null;
        })
        .filter(Boolean) as string[];
};

/**
 * 刷新图层字体素材 id 列表
 * @param fontService - 字体服务
 * @param layer - 图层
 * @returns
 */
export const refreshFontMaterialIDs = (fontService: FontService, layer: ILayerProps): ILayerProps => {
    const fontFamilyIDs = collectFontMaterialIDs(fontService, layer);

    const prev = layer.materialIds || {};
    const next = {
        ...prev,
        [MaterialIdsType.FONT_FAMILY]: fontFamilyIDs,
    };

    return { ...layer, materialIds: next };
};
